/**
 * The viewer engine is packages/render (WebGL2, Nanome's look). The StubEngine in @nanome-lite/state exists for unit
 * tests only. Host callbacks the renderer cannot provide itself:
 *   - filters: installed by bindEngine (the store's cached, worker-backed atom sets);
 *   - cartoon geometry: @nanome-lite/ribbon (the RibbonBuilder.cs port), built in a module Web Worker
 *     (@nanome-lite/ribbon/client; in-thread when workers are unavailable). The render engine accepts the async
 *     builder; any other ViewerEngine gets the synchronous buildRibbonMeshes (the packages/state contract);
 *   - molecular surfaces: @nanome-lite/surface (SESAM wasm in a worker, EDT fallback), one request per chain so
 *     chains pop in as they finish; subsurface (pocket) requests surface the whole chain and cut to the pocket atoms.
 */
import { createEngine, type AsyncRibbonBuilder, type LiteEngine } from '@nanome-lite/render';
import { buildRibbonMeshes } from '@nanome-lite/ribbon';
import { createRibbonClient } from '@nanome-lite/ribbon/client';
import { createSurfaceClient, radiiFromModel, subsurfaceRequest, type SurfaceMesh } from '@nanome-lite/surface/client';
import type { LiteStore, SurfaceBuffers, SurfaceProvider, ViewerEngine, VisualizationModel } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';

export function makeEngine(): LiteEngine {
  const engine = createEngine();
  // Dev builds expose the engine for console inspection and frame timing.
  if (import.meta.env.DEV) (globalThis as { __liteEngine?: LiteEngine }).__liteEngine = engine;
  return engine;
}

/** The render package's engine takes builders that return a Promise; the ViewerEngine contract only sync ones. */
function acceptsAsyncRibbons(engine: ViewerEngine): engine is LiteEngine {
  return typeof (engine as Partial<LiteEngine>).requestRender === 'function' && typeof engine.setRibbonBuilder === 'function';
}

/** SurfaceMesh (packages/surface) -> SurfaceBuffers (engine contract); the arrays are shared with the surface cache (read-only). */
function toSurfaceBuffers(mesh: SurfaceMesh, vis: VisualizationModel): SurfaceBuffers {
  return { position: mesh.position, normal: mesh.normal, atomIndex: mesh.atomIndex, triangles: mesh.triangles, opacity: vis.opacity ?? 1, wireframe: vis.wireframe ?? false };
}

/**
 * Host capabilities the ribbon and surface paths depend on. The regular site uses the defaults (ribbon worker, SESAM
 * wasm in a module worker); the ChatGPT widget build passes `worker: false` when a same-origin module worker cannot
 * be created (ribbons and surfaces then build in-thread) and `forceEdt: true` when WebAssembly is blocked by the
 * host's CSP (src/widget/capabilities.ts).
 */
export interface SurfaceOptions { worker?: false; forceEdt?: boolean }

export function createSurfaceProvider(store: LiteStore, options: SurfaceOptions = {}): { provider: SurfaceProvider; dispose: () => void } {
  const client = createSurfaceClient(options.worker === false ? { worker: false } : {});
  const edt = options.forceEdt ? { forceEdt: true } : {};
  const radii = new WeakMap<NanomeModel, Float32Array>();
  const radiiOf = (model: NanomeModel) => {
    let r = radii.get(model);
    if (!r) { r = radiiFromModel(model); radii.set(model, r); }
    return r;
  };
  const provider: SurfaceProvider = async (_component, entrySerial, vis, atomIndices, { onMesh, signal }) => {
    const model = store.models.get(entrySerial);
    if (!model || atomIndices.length === 0) return null;
    const radius = radiiOf(model);
    if (vis.subsurface) {
      // RenderingViewModel.Surface.cs:53-91: surface the polymer atoms of the touched chains, keep the pocket's vertices
      const mesh = await client.compute(subsurfaceRequest(model, atomIndices, radius, { signal, ...edt }));
      return [toSurfaceBuffers(mesh, vis)];
    }
    // RenderingViewModel.Surface.cs:48-140: one surface per chain (separate meshes also sort transparency per chain)
    const byChain = new Map<number, number[]>();
    for (let k = 0; k < atomIndices.length; k++) {
      const a = atomIndices[k]!;
      const c = model.chainIndex[a]!;
      let list = byChain.get(c);
      if (!list) { list = []; byChain.set(c, list); }
      list.push(a);
    }
    await Promise.all([...byChain.values()].map(async (atoms) => {
      const mesh = await client.compute({ xyz: model.xyz, radius, chainIndex: model.chainIndex, atomIndices: Uint32Array.from(atoms), signal, ...edt });
      onMesh(toSurfaceBuffers(mesh, vis));
    }));
    return [];
  };
  return { provider, dispose: () => client.dispose() };
}

/**
 * Install the ribbon builder and the surface provider; returns the teardown. The filter evaluator comes with bindEngine.
 * `options` carries the widget's capability flags (see SurfaceOptions); the site passes none.
 */
export function installHostCallbacks(engine: ViewerEngine, store: LiteStore, options: SurfaceOptions = {}): () => void {
  let ribbons: ReturnType<typeof createRibbonClient> | null = null;
  if (acceptsAsyncRibbons(engine)) {
    // `worker: false` (the widget when module workers are cross-origin) builds ribbons in-thread, like the surfaces.
    const client = createRibbonClient(options.worker === false ? { worker: false } : {});
    ribbons = client;
    const builder: AsyncRibbonBuilder = (model, residueIndices, colorOf, opts) => client.build(model, residueIndices, colorOf, opts);
    engine.setRibbonBuilder(builder);
  } else {
    engine.setRibbonBuilder?.(buildRibbonMeshes);
  }
  const surfaces = createSurfaceProvider(store, options);
  engine.setSurfaceProvider?.(surfaces.provider);
  return () => {
    engine.setSurfaceProvider?.(null);
    engine.setRibbonBuilder?.(null);
    ribbons?.dispose();
    surfaces.dispose();
  };
}
